import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CloudApiService } from '../../services/cloud-api.service';

@Component({
    selector: 'app-version-info-dialog',
    standalone: true,
    imports: [CommonModule, DialogModule, ButtonModule],
    template: `
        <p-dialog 
            [(visible)]="visible" 
            [modal]="true"
            [closable]="false"
            [draggable]="false" 
            [resizable]="false"
            [style]="{width: '550px'}"
            header="System Information">
            <div class="version-info-content">
                @if (loading()) {
                    <div class="loading-state">
                        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
                        <p>Loading system information...</p>
                    </div>
                } @else if (error()) {
                    <div class="error-state">
                        <i class="pi pi-exclamation-triangle" style="font-size: 2rem; color: var(--red-500);"></i>
                        <p>Failed to load system information</p>
                        <p class="error-message">{{ error() }}</p>
                    </div>
                } @else {
                    <div class="info-sections">
                        <div class="info-section">
                            <h4>Version</h4>
                            <div class="version-details">
                                <div class="version-item">
                                    <label>API Version:</label>
                                    <span class="version-value">{{ versionInfo()?.apiVersion || 'N/A' }}</span>
                                </div>
                                <div class="version-item">
                                    <label>UI Version:</label>
                                    <span class="version-value">{{ versionInfo()?.uiVersion || 'N/A' }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="info-section">
                            <h4>WebSocket Status</h4>
                            <div class="version-details">
                                <div class="version-item">
                                    <label>Connected Clients:</label>
                                    <span class="version-value">{{ wsStats()?.totalClients || 0 }}</span>
                                </div>
                                <div class="version-item">
                                    <label>Active Topics:</label>
                                    <span class="version-value">{{ wsStats()?.totalTopics || 0 }}</span>
                                </div>
                                @if (wsStats()?.topics && wsStats()!.topics.length > 0) {
                                    <div class="topics-list">
                                        @for (topic of wsStats()!.topics; track topic.topic) {
                                            <div class="topic-item">
                                                <span class="topic-name">{{ topic.topic }}</span>
                                                <span class="topic-count">{{ topic.subscriberCount }} subscriber(s)</span>
                                            </div>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
            <ng-template #footer>
                <p-button label="Close" (onClick)="close()" />
            </ng-template>
        </p-dialog>
    `,
    styles: [`
        .version-info-content {
            padding: 1rem 0;
        }

        .loading-state,
        .error-state {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
            padding: 2rem 0;
        }

        .error-message {
            font-size: 0.875rem;
            color: var(--text-color-secondary);
        }

        .info-sections {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .info-section h4 {
            margin: 0 0 0.75rem 0;
            font-size: 0.875rem;
            font-weight: 600;
            color: var(--primary-color);
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .version-details {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .version-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem;
            background: var(--app-surface-inset);
            border-radius: 6px;
        }

        .version-item label {
            font-weight: 600;
            color: var(--text-color);
        }

        .version-value {
            font-family: monospace;
            font-size: 0.95rem;
            color: var(--text-color-secondary);
        }

        .topics-list {
            margin-top: 0.5rem;
            padding: 0.75rem;
            background: var(--app-surface-inset-strong);
            border-radius: 6px;
            max-height: 200px;
            overflow-y: auto;
        }

        .topic-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem;
            border-bottom: 1px solid var(--surface-200);
        }

        .topic-item:last-child {
            border-bottom: none;
        }

        .topic-name {
            font-family: monospace;
            font-size: 0.85rem;
            color: var(--text-color);
        }

        .topic-count {
            font-size: 0.85rem;
            color: var(--text-color-secondary);
        }
    `]
})
export class VersionInfoDialogComponent {
    private cloudApi = inject(CloudApiService);

    visible = false;
    loading = signal(false);
    error = signal<string | null>(null);
    versionInfo = signal<{ apiVersion: string; uiVersion: string } | null>(null);
    wsStats = signal<any>(null);

    open() {
        this.visible = true;
        this.loadSystemInfo();
    }

    close() {
        this.visible = false;
    }

    private loadSystemInfo() {
        this.loading.set(true);
        this.error.set(null);

        // Load both version info and WebSocket stats in parallel
        Promise.all([
            this.cloudApi.getVersionInfo(),
            this.cloudApi.getWebSocketStats()
        ])
            .then(([versionInfo, wsStats]) => {
                this.versionInfo.set(versionInfo);
                this.wsStats.set(wsStats);
                this.loading.set(false);
            })
            .catch(err => {
                this.error.set(err?.message || 'Unknown error occurred');
                this.loading.set(false);
            });
    }
}
